import { Fragment, memo, type ReactNode } from 'react';

/**
 * 답변 말풍선의 <b>가벼운 마크다운</b> (2026-09-11 · DOC-20 §1.31).
 *
 * <h2>왜 직접 그리나</h2>
 *
 * 말풍선이 오래 `pre-wrap` 평문이었다. 에이전트가 표·목록·굵게를 써도 기호가 그대로 보였고,
 * 그래서 프롬프트가 "짧고 담백하게" 만 시켰다 — 그리지 못하니 쓰게 할 수 없었다. 대화를 열면서
 * (chat 노드가 초안·요약·표 정리를 하게 되면서) 그릴 자리가 필요해졌다.
 *
 * <p>라이브러리를 들이지 않는다. 필요한 것은 <b>여섯 가지</b>뿐이고(제목·굵게·인라인 코드·코드
 * 블록·목록·표), 그 이상은 이 화면이 그릴 일이 없다. HTML 은 해석하지 않는다 — 모델 출력이
 * 그대로 DOM 이 되는 길을 열지 않는다.
 *
 * <h2>흘러오는 중에도 그린다</h2>
 *
 * 스트리밍 중에는 코드 블록이 닫히지 않은 채로 올 수 있다. 닫히지 않은 블록은 끝까지를 블록으로
 * 본다 — 다음 조각이 오면 다시 그린다. 표도 마찬가지로 줄 단위라 도중에 그려도 깨지지 않는다.
 */
export const Markdown = memo(function Markdown({ text }: { text: string }) {
  return <>{renderBlocks(text)}</>;
});

const TABLE_ROW = /^\s*\|.*\|\s*$/;
const TABLE_SEP = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;
const UL = /^\s*[-*•·]\s+(.*)$/;
const OL = /^\s*(\d+)[.)]\s+(.*)$/;
const HEADING = /^\s*(#{1,4})\s+(.*)$/;

function renderBlocks(text: string): ReactNode[] {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  const at = (n: number) => lines[n] ?? '';
  const out: ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = at(i);

    // 코드 블록. 닫는 줄이 없으면 끝까지가 블록이다(스트리밍 중).
    if (line.trim().startsWith('```')) {
      const body: string[] = [];
      i += 1;
      while (i < lines.length && !at(i).trim().startsWith('```')) {
        body.push(at(i));
        i += 1;
      }
      i += 1;
      out.push(
        <pre key={key++} className="rounded-lg px-3 py-2 my-1 overflow-x-auto text-[12px]" style={{ background: 'var(--bg-hover)' }}>
          <code>{body.join('\n')}</code>
        </pre>,
      );
      continue;
    }

    // 표 — 머리글 줄 + 구분선 줄이 있어야 표다. 구분선이 없으면 그냥 글이다.
    if (TABLE_ROW.test(line) && i + 1 < lines.length && TABLE_SEP.test(at(i + 1))) {
      const header = cells(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && TABLE_ROW.test(at(i))) {
        rows.push(cells(at(i)));
        i += 1;
      }
      out.push(
        <div key={key++} className="overflow-x-auto my-1">
          <table className="text-[12.5px] border-collapse">
            <thead>
              <tr>
                {header.map((h, n) => (
                  <th key={n} className="px-2 py-1 text-left font-semibold whitespace-nowrap" style={{ borderBottom: '1px solid var(--border)' }}>
                    {inline(h)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, r) => (
                <tr key={r}>
                  {header.map((_, n) => (
                    <td key={n} className="px-2 py-1 align-top" style={{ borderBottom: '1px solid var(--border)' }}>
                      {inline(row[n] ?? '')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    // 목록 — 이어지는 줄을 하나로 묶는다.
    if (UL.test(line) || OL.test(line)) {
      const ordered = OL.test(line);
      const items: string[] = [];
      while (i < lines.length && (ordered ? OL.test(at(i)) : UL.test(at(i)))) {
        const m = ordered ? OL.exec(at(i)) : UL.exec(at(i));
        items.push((ordered ? m?.[2] : m?.[1]) ?? '');
        i += 1;
      }
      const Tag = ordered ? 'ol' : 'ul';
      out.push(
        <Tag key={key++} className={`${ordered ? 'list-decimal' : 'list-disc'} pl-5 my-1 space-y-0.5`}>
          {items.map((item, n) => <li key={n}>{inline(item)}</li>)}
        </Tag>,
      );
      continue;
    }

    const h = HEADING.exec(line);
    if (h) {
      out.push(<div key={key++} className="font-semibold mt-2 mb-0.5">{inline(h[2] ?? '')}</div>);
      i += 1;
      continue;
    }

    // 평문 줄. 빈 줄은 문단 간격이다.
    if (line.trim() === '') {
      out.push(<div key={key++} style={{ height: '0.5em' }} />);
    } else {
      out.push(<div key={key++}>{inline(line)}</div>);
    }
    i += 1;
  }
  return out;
}

function cells(row: string): string[] {
  const trimmed = row.trim().replace(/^\|/, '').replace(/\|$/, '');
  return trimmed.split('|').map((c) => c.trim());
}

const INLINE = /(\*\*[^*]+\*\*|`[^`]+`)/g;

/** 굵게·인라인 코드만. 링크·기울임은 이 화면이 그릴 일이 없다. */
function inline(text: string): ReactNode {
  const parts = text.split(INLINE);
  if (parts.length === 1) return text;
  return parts.map((part, n) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={n}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      return <code key={n} className="px-1 rounded text-[12px]" style={{ background: 'var(--bg-hover)' }}>{part.slice(1, -1)}</code>;
    }
    return <Fragment key={n}>{part}</Fragment>;
  });
}
